import { el, mount } from './dom';
import { has, t } from '@core/i18n';
import { getGame } from '@core/game-registry';
import { TurnManager } from '@core/turn-manager';
import { loadSession, saveSession, recordStat, deleteSession } from '@core/session';
import { navigate } from './router';
import { assetUrl } from './assets';
import type { GameView } from '@core/types';

export async function renderPlay(gameId: string, sessionId: string): Promise<HTMLElement> {
  const game = getGame(gameId);
  if (!game) {
    return el('div', { class: 'p-6' }, [`Unknown game: ${gameId}`]);
  }

  const session = await loadSession(gameId, sessionId);
  if (!session) {
    queueMicrotask(() => navigate({ name: 'lobby' }));
    return el('div', { class: 'p-6' }, ['Session not found']);
  }

  // Carica gli asset in background: la UI viene mostrata subito,
  // input e cronologia diventano interattivi quando gli asset sono pronti.
  let assetsReady = false;
  const assetsPromise = game.loadAssets().then(() => {
    assetsReady = true;
  });
  const tm = TurnManager.restore(game, session);

  const root = el('div', { class: 'min-h-dvh', dataset: { theme: game!.id } });

  let handoffPending = tm.config.mode !== 'single';
  let lastFeedback = '';
  let lastError = '';

  function gameText(suffix: string, fallbackKey: string, vars: Record<string, string | number> = {}): string {
    const gameKey = `${game!.id}.${suffix}`;
    return t(has(gameKey) ? gameKey : fallbackKey, vars);
  }

  const boardContainer = el('div',
    game.id === 'globe'
      ? { class: 'card globe-board w-full max-w-xs sm:max-w-sm mx-auto aspect-square relative overflow-hidden p-0' }
      : { class: 'w-full' },
  );
  let view: GameView | null = null;

  function showBoardLoading(): void {
    boardContainer.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'absolute inset-0 grid place-items-center text-slate-400 text-sm';
    loading.textContent = t('globe.loading');
    // Posizionamento relativo se non è il riquadro globo
    if (game!.id !== 'globe') {
      const wrap = document.createElement('div');
      wrap.className = 'relative w-full min-h-[12rem]';
      wrap.appendChild(loading);
      boardContainer.appendChild(wrap);
    } else {
      boardContainer.appendChild(loading);
    }
  }

  function rebuildBoard(): void {
    if (!assetsReady) {
      showBoardLoading();
      return;
    }
    if (view) view.destroy();
    view = game!.renderBoard(boardContainer, tm.snapshot().state, {
      onGuess: (g) => void submit(typeof g === 'string' ? g : JSON.stringify(g)),
      onRequestHint: game!.revealHint
        ? () => {
            tm.patchState((s) => game!.revealHint!(s));
            void saveSession(tm.serialize());
            if (view?.update) view.update(tm.snapshot().state);
            else rebuildBoard();
          }
        : undefined,
    });
  }

  async function submit(raw: string): Promise<void> {
    if (handoffPending) return;
    if (!assetsReady) {
      // Aspetta che gli asset siano caricati prima di processare la guess.
      await assetsPromise;
    }
    const r = tm.submit(raw);
    if (!r.ok) {
      lastError =
        r.reason === 'duplicate'
          ? t(has(`${game!.id}.duplicate_guess`) ? `${game!.id}.duplicate_guess` : 'common.duplicate_guess')
          : t(has(`${game!.id}.unknown_guess`) ? `${game!.id}.unknown_guess` : 'common.unknown_guess');
      // In caso di duplicato il turno NON avanza: stesso giocatore riprova.
      render();
      return;
    }
    lastError = '';
    lastFeedback = r.result.feedback ?? '';
    await saveSession(tm.serialize());
    if (r.won) {
      const snap = tm.snapshot();
      const winner = snap.history[snap.history.length - 1];
      if (winner) {
        await recordStat({
          gameType: game!.id,
          entityId: winner.byEntity.id,
          attempts: snap.history.length,
          won: true,
        });
      }
      renderWin();
      return;
    }
    handoffPending = tm.config.mode !== 'single';
    if (view?.update) {
      view.update(tm.snapshot().state);
    } else {
      rebuildBoard();
    }
    render();
  }

  async function surrender(): Promise<void> {
    if (!confirm(gameText('surrender.confirm', 'common.surrender.confirm'))) return;
    tm.surrender();
    await saveSession(tm.serialize());
    if (view?.update) view.update(tm.snapshot().state);
    handoffPending = false;
    renderEnd();
  }

  async function newGame(): Promise<void> {
    await deleteSession(game!.id, sessionId);
    navigate({ name: 'setup', gameId: game!.id });
  }

  function renderHistory(): HTMLElement {
    const snap = tm.snapshot();
    // Ordina per distanza crescente (più vicino in cima); le guess senza km (es. vittoria) vanno in fondo.
    const items = snap.history.slice().sort((a, b) => {
      const ka = a.result.score ?? Infinity;
      const kb = b.result.score ?? Infinity;
      return ka - kb;
    });
    return el(
      'div',
      { class: 'card globe-history-card max-h-64 overflow-y-auto' },
      items.length === 0
        ? [el('p', { class: 'text-slate-400 text-sm' }, ['—'])]
        : items.map((h) => {
            const guess = h.guess as { name?: string; cca2?: string; flag?: string; iso3?: string };
            const guessName = guess.name ?? String(h.guess);
            const cca2 = guess.cca2?.toLowerCase() ?? '';
            const flagEmoji = guess.flag ?? '';
            const km = h.result.score;
            const color = (h.result.meta as { color?: string } | undefined)?.color ?? '#94a3b8';

            // <img> con bandiera SVG ufficiale; se fallisce (SVG mancante) mostra l'emoji.
            const flagEl = (() => {
              if (cca2) {
                const img = document.createElement('img');
                img.src = assetUrl(`flags/${cca2}.svg`);
                img.setAttribute('width', '20');
                img.setAttribute('height', '14');
                img.className = 'inline-block shrink-0 rounded-sm object-cover';
                img.alt = guessName;
                const fallback = el('span', { class: 'shrink-0 text-base leading-none hidden' }, [flagEmoji]);
                img.addEventListener('error', () => {
                  img.classList.add('hidden');
                  fallback.classList.remove('hidden');
                });
                const wrap = el('span', { class: 'shrink-0 flex items-center' });
                wrap.append(img, fallback);
                return wrap;
              }
              return flagEmoji ? el('span', { class: 'shrink-0 text-base leading-none' }, [flagEmoji]) : null;
            })();

            return el(
              'div',
              {
                class:
                  'flex items-center gap-2 py-1 border-b border-white/5 last:border-0 text-sm cursor-pointer hover:bg-white/5 rounded transition-colors',
                on: {
                  click: () => {
                    const iso3 = guess.iso3;
                    if (iso3) view?.focusCountry?.(iso3);
                  },
                },
              },
              [
                el('span', {
                  class: 'inline-block w-3 h-3 rounded-full shrink-0',
                  style: `background:${color}`,
                }),
                flagEl,
                el('span', { class: 'flex-1 truncate font-medium' }, [guessName]),
                km !== undefined
                  ? el('span', { class: 'text-xs tabular-nums shrink-0', style: `color:${color}` }, [
                      `${km.toLocaleString()} km`,
                    ])
                  : null,
              ],
            );
          }),
    );
  }

  function handoffOverlay(): HTMLElement {
    const snap = tm.snapshot();
    return el(
      'div',
      {
        class:
          'absolute inset-0 z-50 bg-bg/95 backdrop-blur flex flex-col items-center justify-center p-6 text-center gap-4',
      },
      [
        el('p', { class: 'text-slate-400 uppercase tracking-widest text-xs' }, [
          t('turn.handoff.subtitle'),
        ]),
        el('h2', { class: 'text-4xl sm:text-6xl font-bold' }, [
          t('turn.handoff.title', { name: snap.entityLabel }),
        ]),
        snap.entity.kind === 'team'
          ? el('p', { class: 'text-slate-300' }, [`(${snap.player.name})`])
          : null,
        el(
          'button',
          {
            class: 'btn-primary mt-4',
            on: {
              click: () => {
                handoffPending = false;
                render();
              },
            },
          },
          [t('turn.handoff.continue')],
        ),
      ],
    );
  }

  function render(): void {
    const snap = tm.snapshot();
    // Datalist per autocomplete (es. animali)
    const acValues = game!.autocompleteValues?.() ?? [];
    const listId = acValues.length > 0 ? `${game!.id}-ac` : undefined;
    if (listId) {
      let dl = document.getElementById(listId) as HTMLDataListElement | null;
      if (!dl) {
        dl = document.createElement('datalist');
        dl.id = listId;
        document.body.appendChild(dl);
      }
      if (dl.children.length === 0) {
        for (const v of acValues) {
          const opt = document.createElement('option');
          opt.value = v;
          dl.appendChild(opt);
        }
      }
    }
    const inputAttrs: Record<string, string> = {
      class: 'input flex-1',
      placeholder: t('turn.guess.placeholder'),
      autocomplete: 'off',
    };
    if (listId) inputAttrs['list'] = listId;
    const input = el('input', {
      ...inputAttrs,
      on: {
        keydown: (ev) => {
          if ((ev as KeyboardEvent).key === 'Enter') {
            const v = (ev.target as HTMLInputElement).value.trim();
            if (v) void submit(v);
            (ev.target as HTMLInputElement).value = '';
          }
        },
      },
    });

    const main = el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6 grid gap-4 relative' }, [
      el('header', { class: 'flex items-center justify-between gap-2' }, [
        el(
          'button',
          { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } },
          ['←'],
        ),
        el('div', { class: 'text-center min-w-0 flex-1' }, [
          el('div', { class: 'text-xs text-slate-400' }, [
            t('turn.attempt', { n: snap.history.length }),
          ]),
          el('div', { class: 'font-semibold truncate flex items-center justify-center gap-1' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-5 h-5 object-contain shrink-0'; return img; })()
              : document.createTextNode(game!.metadata.icon + ' '),
            document.createTextNode(snap.entityLabel),
          ]),
        ]),
        el('div', { class: 'flex gap-1' }, [
          el(
            'button',
            {
              class: 'btn-ghost text-xs',
              title: gameText('surrender', 'common.surrender'),
              on: { click: () => void surrender() },
            },
            ['🏳️'],
          ),
          el(
            'button',
            {
              class: 'btn-ghost text-xs',
              title: gameText('new_game', 'common.new_game'),
              on: { click: () => void newGame() },
            },
            ['🔄'],
          ),
        ]),
      ]),
      boardContainer,
      el('div', { class: 'theme-divider' }),
      el('div', { class: 'flex gap-2' }, [
        input,
        el(
          'button',
          {
            class: 'btn-primary',
            on: {
              click: () => {
                const v = input.value.trim();
                if (v) void submit(v);
                input.value = '';
              },
            },
          },
          [t('turn.guess.submit')],
        ),
      ]),
      lastError
        ? el('div', { class: 'text-rose-300 text-sm' }, [lastError])
        : lastFeedback
          ? el('div', { class: 'theme-feedback text-sm' }, [lastFeedback])
          : null,
      game!.metadata.rendersOwnHistory ? null : renderHistory(),
      handoffPending ? handoffOverlay() : null,
    ]);

    mount(root, main);
  }

  function renderWin(): void {
    const snap = tm.snapshot();
    const winner = snap.history[snap.history.length - 1];
    const winnerName = winner
      ? tm.config.players.find((p) => p.id === winner.byPlayerId)?.name ?? '?'
      : '?';
    mount(
      root,
      el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6' }, [
        el('div', { class: 'grid gap-4 text-center mt-12' }, [
          el('div', { class: 'text-6xl' }, ['🎉']),
          el('h1', { class: 'text-3xl font-bold theme-win-title' }, [t('win.title')]),
          el('p', { class: 'text-slate-300' }, [
            `${winnerName} • ${t('win.summary', { n: snap.history.length })}`,
          ]),
          el('div', { class: 'flex gap-2 justify-center mt-4' }, [
            el(
              'button',
              {
                class: 'btn-ghost',
                on: {
                  click: async () => {
                    await deleteSession(game!.id, sessionId);
                    navigate({ name: 'lobby' });
                  },
                },
              },
              [t('win.back')],
            ),
            el(
              'button',
              {
                class: 'btn-primary',
                on: {
                  click: async () => {
                    await deleteSession(game!.id, sessionId);
                    navigate({ name: 'setup', gameId: game!.id });
                  },
                },
              },
              [t('win.replay')],
            ),
          ]),
        ]),
      ]),
    );
  }

  function renderEnd(): void {
    const snap = tm.snapshot();
    const targetLabel = tm.getTargetLabel();
    mount(
      root,
      el('main', { class: 'mx-auto max-w-3xl p-4 sm:p-6 grid gap-4' }, [
        el('header', { class: 'flex items-center justify-between gap-2' }, [
          el(
            'button',
            { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } },
            ['←'],
          ),
          el('div', { class: 'text-center font-semibold flex-1 flex items-center justify-center gap-1' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-5 h-5 object-contain shrink-0'; return img; })()
              : document.createTextNode(game!.metadata.icon + ' '),
            document.createTextNode(gameText('surrendered.title', 'common.surrendered.title')),
          ]),
          el('span', { class: 'w-10' }, ['']),
        ]),
        el('div', { class: 'theme-divider' }),
        boardContainer,
        targetLabel
          ? el('div', { class: 'card text-center' }, [
              el('p', { class: 'text-slate-400 text-xs uppercase tracking-widest' }, [
                gameText('surrendered.title', 'common.surrendered.title'),
              ]),
              el('p', { class: 'text-xl font-bold mt-2' }, [
                gameText('target_was', 'common.target_was', { name: targetLabel }),
              ]),
              el('p', { class: 'text-slate-400 text-sm mt-1' }, [
                t('win.summary', { n: snap.history.length }),
              ]),
            ])
          : null,
        el('div', { class: 'flex gap-2 justify-center' }, [
          el(
            'button',
            {
              class: 'btn-ghost',
              on: {
                click: async () => {
                  await deleteSession(game!.id, sessionId);
                  navigate({ name: 'lobby' });
                },
              },
            },
            [t('win.back')],
          ),
          el(
            'button',
            {
              class: 'btn-primary',
              on: { click: () => void newGame() },
            },
            [gameText('new_game', 'common.new_game')],
          ),
        ]),
        game!.metadata.rendersOwnHistory ? null : renderHistory(),
      ]),
    );
  }

  rebuildBoard();
  // Se la sessione è stata caricata già finita (vinta o resa), salta direttamente alla schermata finale.
  if (tm.isGameOver()) {
    if (tm.isSurrendered()) renderEnd();
    else renderWin();
  } else {
    render();
  }

  // Quando gli asset sono pronti, costruisci la board reale e ri-renderizza
  // (per popolare datalist autocomplete, abilitare input, ecc.).
  void assetsPromise.then(() => {
    rebuildBoard();
    if (!tm.isGameOver()) render();
  });

  return root;
}
